import React, { useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Typography, Box, CircularProgress } from '@mui/material'
import { Report } from '@/core/models/report.model'
import { Patient } from '@/core/models/patient.model'
import { useGetPatientHealthRecordQuery } from '@/core/services/report'
import { useGetPatientQuery, useUpdatePatientMutation } from '@/core/services/patient'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { skipToken } from '@reduxjs/toolkit/query'
import _ from 'lodash'

type PatientProfileSectionProps = {
  title: string
  patient?: Pick<Patient, 'vaccins' | 'background' | 'allergy' | 'treatment'>
  isLoading?: boolean
}

export function PatientProfileSection({ title, patient, isLoading }: PatientProfileSectionProps) {
  useLingui()
  return (
    <Box sx={{ my: 2 }}>
      <Divider sx={{ mb: 2 }}>{title}</Divider>
      {isLoading ? (
        <CircularProgress size={20} title={t`Loading...`} />
      ) : _.isEmpty(patient) ? (
        <Typography>
          <Trans>No data to update</Trans>
        </Typography>
      ) : (
        <>
          {patient?.allergy && (
            <Typography variant="body2">
              {t`Allergy`}: {patient?.allergy}
            </Typography>
          )}
          {patient?.background && (
            <Typography variant="body2">
              {t`Background`}: {patient?.background}
            </Typography>
          )}
          {patient?.treatment && (
            <Typography variant="body2">
              {t`Treatment`}: {patient?.treatment}
            </Typography>
          )}
          {patient?.vaccins && (
            <Typography variant="body2">
              {t`Vaccines`}: {patient?.vaccins}
            </Typography>
          )}
        </>
      )}
    </Box>
  )
}

export type PatientContextAlertProps = {
  report: Report
}

export default function UpdatePatientRecord({ report }: PatientContextAlertProps) {
  useLingui()
  const [isDialogOpen, setDialogOpen] = useState(false)
  const { data: patient, refetch } = useGetPatientQuery(typeof report.patient === 'string' ? report.patient : report.patient._id, {
    refetchOnMountOrArgChange: true,
  })
  const {
    data: patientHealthRecord,
    isFetching: isGetting,
    refetch: refetchHealthRecord,
  } = useGetPatientHealthRecordQuery(report._id ?? skipToken, {
    refetchOnMountOrArgChange: true,
  })

  const [updatePatient, { isLoading: isUpdating }] = useUpdatePatientMutation()

  const handleUpdate = async () => {
    if (patientHealthRecord) {
      await updatePatient({
        id: typeof report.patient === 'string' ? report.patient : report.patient._id,
        ...patientHealthRecord,
      }).unwrap()
      refetch()
    }
    setDialogOpen(false)
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          refetchHealthRecord()
          setDialogOpen(true)
        }}
        aria-label="Update patient profile"
      >
        <Trans>Update Patient</Trans>
      </Button>

      <Dialog open={isDialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Trans>Patient Profile Update</Trans>
        </DialogTitle>

        <DialogContent>
          <PatientProfileSection title={t`Current Profile`} patient={patient!} />
          <PatientProfileSection title={t`Proposed Updates`} patient={patientHealthRecord} isLoading={isGetting} />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setDialogOpen(false)
            }}
            color="inherit"
            disabled={isUpdating}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={handleUpdate}
            color="primary"
            variant="contained"
            disabled={_.isEmpty(patientHealthRecord) || isUpdating || isGetting}
            startIcon={isUpdating && <CircularProgress size={20} />}
          >
            <Trans>{isUpdating ? 'In Progress...' : 'Update'}</Trans>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
